import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import api from "@/lib/api";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "coverage",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const i = params.get("interest");
    if (i === "career" || i === "coverage") {
      setForm((f) => ({ ...f, interest: i }));
    }
  }, [params]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        interest: form.interest,
        message: form.message.trim() || null,
      });
      toast.success("Thank you. Amiee will reach out shortly.");
      setForm({ name: "", email: "", phone: "", interest: form.interest, message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-32">
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* LEFT */}
          <div className="lg:col-span-5">
            <div className="overline mb-6">— Contact Us</div>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-white leading-[0.95] tracking-tighter mb-8">
              Let's <span className="italic text-[#D4AF37]">connect.</span>
            </h1>
            <p className="text-white/70 leading-relaxed mb-12 max-w-md">
              Have questions about coverage or career opportunities? We would love to connect with you.
            </p>

            <div className="space-y-px bg-white/5">
              <div className="bg-[#0D0D0D] p-6 flex items-center gap-5">
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="overline mb-1">Amiee Davis</div>
                  <a href="tel:9364435365" data-testid="contact-phone" className="text-white text-lg hover:text-[#D4AF37] transition-colors">936-443-5365</a>
                </div>
              </div>
              <div className="bg-[#0D0D0D] p-6 flex items-center gap-5">
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="overline mb-1">Email</div>
                  <a href="mailto:impactlegacy.davis@gmail.com" data-testid="contact-email" className="text-white hover:text-[#D4AF37] transition-colors break-all">impactlegacy.davis@gmail.com</a>
                </div>
              </div>
              <div className="bg-[#0D0D0D] p-6 flex items-center gap-5">
                <div className="w-11 h-11 border border-[#D4AF37]/40 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="overline mb-1">Location</div>
                  <div className="text-white">Texas</div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT - FORM */}
          <div className="lg:col-span-7 lg:pl-8 lg:border-l lg:border-white/5">
            <form data-testid="contact-form" onSubmit={submit} className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="overline mb-3 block">Full Name</label>
                  <input
                    type="text"
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    className="input-luxury"
                    placeholder="Your name"
                    required
                  />
                </div>
                <div>
                  <label className="overline mb-3 block">Email</label>
                  <input
                    type="email"
                    data-testid="contact-input-email"
                    value={form.email}
                    onChange={update("email")}
                    className="input-luxury"
                    placeholder="you@email.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="overline mb-3 block">Phone</label>
                  <input
                    type="tel"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input-luxury"
                    placeholder="(optional)"
                  />
                </div>
                <div>
                  <label className="overline mb-3 block">I'm Interested In</label>
                  <div className="relative">
                    <select
                      data-testid="contact-input-interest"
                      value={form.interest}
                      onChange={update("interest")}
                      className="input-luxury appearance-none pr-8 cursor-pointer"
                      style={{ colorScheme: "dark" }}
                    >
                      <option value="coverage" style={{ background: "#1A1A1A" }}>I'm Looking For Coverage</option>
                      <option value="career" style={{ background: "#1A1A1A" }}>I'm Interested In A Career</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-[#D4AF37] absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div>
                <label className="overline mb-3 block">Message</label>
                <textarea
                  data-testid="contact-input-message"
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  className="input-luxury resize-none"
                  placeholder="Tell us how we can help..."
                />
              </div>

              <button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={loading}
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {loading ? "Sending..." : (<>Let's Connect <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
